// Statuswechsel einer Opportunity (Ziehen im Board, Menü in Karte und Liste): ein Aufruf, Rückmeldung unten links mit
// „Rückgängig“ und den Hinweisen des Servers. Die Seiten geben an, was sofort in der Anzeige passiert und wie sie bei einem
// Fehler zurückgehen.
import { useCallback } from "react";
import { opportunitiesApi, type OpportunityZeile } from "../api/opportunities";
import { useNutzer } from "../auth/AuthContext";
import { useMeldung } from "../components/Meldungen";

export interface WechselHaken {
  /** sofort, vor der Antwort des Servers (Karte in die neue Spalte legen) */
  sofort?: (opp: OpportunityZeile, statusId: string) => void;
  /** Fehler: zurück auf den alten Stand */
  rollback?: () => void;
  /** Erfolg: die Opportunity, wie der Server sie liefert */
  fertig?: (neu: OpportunityZeile) => void;
}

export function useStatusWechsel() {
  const meldung = useMeldung();
  return useCallback(
    async function wechseln(opp: OpportunityZeile, statusId: string, haken: WechselHaken = {}): Promise<void> {
      if (opp.status_id === statusId) return;
      const alt = opp.status_id;
      haken.sofort?.(opp, statusId);
      try {
        const r = await opportunitiesApi.aendern(opp.id, { status_id: statusId });
        haken.fertig?.(r.opportunity);
        meldung.zeige(
          "ok",
          `„${opp.lead_name ?? "Opportunity"}“ steht jetzt auf „${r.opportunity.status_label ?? "neuer Status"}“.`,
          alt ? { label: "Rückgängig", onClick: () => void wechseln(r.opportunity, alt, haken) } : undefined,
          8000,
        );
        for (const h of r.hinweise ?? []) meldung.zeige("info", h, undefined, 9000);
      } catch (e) {
        haken.rollback?.();
        meldung.fehler(e, "Statuswechsel nicht möglich");
      }
    },
    [meldung],
  );
}

/** Darf der Angemeldete diese Opportunity ändern? Eigene immer, fremde mit dem Close-Recht „manage_others_opportunities“. */
export function useDarfAendern(): (opp: OpportunityZeile) => boolean {
  const ich = useNutzer();
  const fremde = ich.permissions?.includes("manage_others_opportunities") ?? false;
  const eigene = new Set([ich.id, ich.close_user_id].filter(Boolean) as string[]);
  return (opp) => fremde || (!!opp.user_id && eigene.has(opp.user_id));
}
